import { createAsyncThunk } from '@reduxjs/toolkit';

import { sharedApi } from 'utils/api/api';
import formatCountryWithCurrencyDictionary, {
  RawCountryWithCurrencyDictionary
} from 'utils/formatCountryWithCurrencyDictionary';
import formatDictionaryData from 'utils/formatDictionaryData';

import { DictionaryPayload, RawDictionaryData } from 'types/dictionaries';

import { DICT } from 'constants/endpoints';
import { Dictionaries } from 'constants/enums';

interface DictionaryResponse {
  name: string;
  data: unknown;
}

const buildDictionaryPayload = (
  response: DictionaryResponse
): DictionaryPayload => {
  if (response.name === Dictionaries.ClientCountryWithCurrency) {
    return {
      storage: 'countryWithCurrency',
      dict: Dictionaries.ClientCountryWithCurrency,
      data: formatCountryWithCurrencyDictionary(
        response.data as RawCountryWithCurrencyDictionary
      )
    };
  }

  return {
    storage: 'data',
    dict: response.name,
    data: formatDictionaryData(response.data as RawDictionaryData)
  };
};

export const fetchDictionary = createAsyncThunk(
  'dictionaries/fetchDictionary',
  async (
    { dict, locale }: { dict: string; locale?: string },
    { rejectWithValue }
  ) => {
    try {
      const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
      const response = await sharedApi.get(`${DICT}/${dict}${query}`);
      return buildDictionaryPayload(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchDictionariesBatch = createAsyncThunk(
  'dictionaries/fetchDictionariesBatch',
  async (
    { dicts, locale }: { dicts: string[]; locale?: string },
    { rejectWithValue }
  ) => {
    try {
      const query = locale ? `&locale=${encodeURIComponent(locale)}` : '';
      const names = dicts.join(',');
      const response = (await sharedApi.get(
        `${DICT}/list?name=${names}${query}`
      )) as DictionaryResponse[];

      return response.map(buildDictionaryPayload);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
